import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

/**
 * Gift Card inline purchase form.
 * Rendered inside the "Gift Card / Series" tab (no more modal-on-modal).
 * Captures Tagline / Style (template swap) / Expires (validity date) /
 * Redeem For (Amount OR a specific Service OR a specific Package) /
 * Quantity. Emits `quantity` gift_card order lines whose meta carries
 * the extra fields so the backend can stamp them on the issued voucher
 * when the order completes.
 */
export default function GiftCardForm({ templates, services, packages, branch, onAdd }) {
  const activeTemplates = useMemo(
    () => (templates || []).filter((t) => t.active !== false),
    [templates]
  );
  const defaultExpiry = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  }, []);

  const [tagline, setTagline] = useState("");
  const [toName, setToName] = useState("");
  const [fromName, setFromName] = useState("");
  const [message, setMessage] = useState("");
  const [templateId, setTemplateId] = useState(activeTemplates[0]?.id || "");
  const [expiresAt, setExpiresAt] = useState(defaultExpiry);
  const [redeemKind, setRedeemKind] = useState("amount"); // amount | service | package
  const [amount, setAmount] = useState("");
  const [serviceQ, setServiceQ] = useState("");
  const [selectedService, setSelectedService] = useState(null);
  const [packageQ, setPackageQ] = useState("");
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [quantity, setQuantity] = useState(1);

  // Reset the template id if the active templates list loads AFTER first render.
  useEffect(() => {
    if (!templateId && activeTemplates[0]?.id) setTemplateId(activeTemplates[0].id);
  }, [activeTemplates, templateId]);

  const svcMatches = useMemo(() => {
    const q = serviceQ.trim().toLowerCase();
    if (!q) return [];
    return services.filter((s) => (s.name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [serviceQ, services]);
  const pkgMatches = useMemo(() => {
    const q = packageQ.trim().toLowerCase();
    if (!q) return [];
    return packages.filter((p) => (p.name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [packageQ, packages]);

  const svcPriceForBranch = (svc) => {
    if (!svc) return 0;
    const ov = Array.isArray(svc.branch_overrides) ? svc.branch_overrides.find((b) => b.branch_id === branch?.id) : null;
    return Number(ov?.price ?? svc.price ?? 0);
  };
  const pkgPriceForBranch = (pkg) => {
    if (!pkg) return 0;
    const ov = Array.isArray(pkg.branch_overrides) ? pkg.branch_overrides.find((b) => b.branch_id === branch?.id) : null;
    return Number(ov?.price ?? pkg.price ?? 0);
  };

  const reset = () => {
    setTagline("");
    setToName("");
    setFromName("");
    setMessage("");
    setExpiresAt(defaultExpiry);
    setRedeemKind("amount");
    setAmount("");
    setServiceQ(""); setSelectedService(null);
    setPackageQ(""); setSelectedPackage(null);
    setQuantity(1);
  };

  const submit = () => {
    const tmpl = activeTemplates.find((t) => t.id === templateId);
    if (!tmpl) { toast.error("Pick a Style/template"); return; }
    // Mandatory personalisation — every printed / emailed card must
    // carry these two identity fields so the recipient sees a real
    // gift. Tagline + Message stay optional so a quick corporate
    // voucher can be sold with just the recipient name.
    if (!toName.trim())   { toast.error("‘A Gift For’ is required"); return; }
    if (!fromName.trim()) { toast.error("‘From’ is required"); return; }
    if (!expiresAt)       { toast.error("Pick an expiry date"); return; }
    const qty = Math.max(1, parseInt(String(quantity || 1), 10));

    let unit_price = 0;
    let redemption_label = "";
    const meta = {
      tagline: tagline.trim(),
      to_name: toName.trim(),
      from_name: fromName.trim(),
      message: message.trim(),
      expires_at: `${expiresAt}T23:59:59Z`,
      redeem_kind: redeemKind,
      // display_kind drives whether the Value/Service slot on the printed
      // A5 card prints the currency amount ("amount") or the linked
      // service/package name ("service") — using the SAME X/Y anchor.
      display_kind: redeemKind === "amount" ? "amount" : "service",
    };

    if (redeemKind === "amount") {
      unit_price = parseInt(String(amount).replace(/\D/g, ""), 10);
      if (!(unit_price > 0)) { toast.error("Enter a voucher amount"); return; }
      redemption_label = `Rp ${unit_price.toLocaleString()}`;
    } else if (redeemKind === "service") {
      if (!selectedService) { toast.error("Pick a service"); return; }
      unit_price = svcPriceForBranch(selectedService);
      redemption_label = selectedService.name;
      meta.redeem_service_id = selectedService.id;
      meta.redeem_service_name = selectedService.name;
    } else if (redeemKind === "package") {
      if (!selectedPackage) { toast.error("Pick a package"); return; }
      unit_price = pkgPriceForBranch(selectedPackage);
      redemption_label = selectedPackage.name;
      meta.redeem_package_id = selectedPackage.id;
      meta.redeem_package_name = selectedPackage.name;
    }

    onAdd({
      kind: "gift_card",
      ref_id: templateId,
      name: `${tmpl.title || "Gift Card"} — ${redemption_label}`,
      quantity: qty,
      unit_price,
      discount: 0,
      meta,
    });
    reset();
  };

  if (activeTemplates.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        No active Gift Card templates yet. Design one in <span className="font-medium">Brand → Gift Card</span>.
      </div>
    );
  }

  return (
    <div className="grid gap-3" data-testid="gift-card-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">A Gift For <span className="text-red-600">*</span></Label>
          <Input
            value={toName}
            onChange={(e) => setToName(e.target.value)}
            maxLength={60}
            placeholder="Recipient name"
            data-testid="gift-purchase-to-name"
          />
        </div>
        <div>
          <Label className="text-xs">From <span className="text-red-600">*</span></Label>
          <Input
            value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            maxLength={60}
            placeholder="Buyer name"
            data-testid="gift-purchase-from-name"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs">Tagline <span className="text-muted-foreground">(optional)</span></Label>
        <Input
          value={tagline}
          onChange={(e) => setTagline(e.target.value)}
          maxLength={80}
          placeholder="e.g. Happy Birthday from Mama"
          data-testid="gift-purchase-tagline"
        />
        <p className="text-[10px] text-muted-foreground mt-0.5">{80 - tagline.length} characters left · stamped on the printed card</p>
      </div>

      <div>
        <Label className="text-xs">Message <span className="text-muted-foreground">(optional)</span></Label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          maxLength={400}
          rows={3}
          placeholder="A short note the recipient sees on the printed card"
          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          data-testid="gift-purchase-message"
        />
        <p className="text-[10px] text-muted-foreground mt-0.5">{400 - message.length} characters left · wraps inside the message zone</p>
      </div>

      <div>
        <Label className="text-xs">Style</Label>
        <Select value={templateId} onValueChange={setTemplateId}>
          <SelectTrigger data-testid="gift-purchase-style"><SelectValue placeholder="Pick a template" /></SelectTrigger>
          <SelectContent>
            {activeTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs">Expires</Label>
        <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} data-testid="gift-purchase-expires" />
        <p className="text-[10px] text-muted-foreground mt-0.5">Stamped on the printed card as the &quot;Valid Until&quot; date.</p>
      </div>

      <div>
        <Label className="text-xs">Redeem for</Label>
        <div className="border border-border rounded-md p-3 space-y-2 bg-muted/20">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="radio" checked={redeemKind === "amount"} onChange={() => setRedeemKind("amount")} data-testid="gift-purchase-redeem-amount" />
            <span className="text-sm w-16">Amount</span>
            <span className="text-xs text-muted-foreground">Rp</span>
            <Input
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(e) => { setRedeemKind("amount"); setAmount(e.target.value); }}
              placeholder="500000"
              className="w-40"
              data-testid="gift-purchase-amount"
            />
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="radio" checked={redeemKind === "service"} onChange={() => setRedeemKind("service")} data-testid="gift-purchase-redeem-service" />
            <span className="text-sm w-16 pt-1">Service</span>
            <div className="flex-1 relative">
              <Input
                value={selectedService ? selectedService.name : serviceQ}
                onChange={(e) => { setRedeemKind("service"); setSelectedService(null); setServiceQ(e.target.value); }}
                placeholder="Type a service name…"
                data-testid="gift-purchase-service-input"
              />
              {redeemKind === "service" && !selectedService && svcMatches.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-md max-h-52 overflow-auto">
                  {svcMatches.map((s) => (
                    <button
                      type="button"
                      key={s.id}
                      onClick={() => { setSelectedService(s); setServiceQ(""); }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted"
                      data-testid={`gift-purchase-service-pick-${s.id}`}
                    >
                      {s.name}
                      <span className="ml-2 text-xs text-muted-foreground">Rp {svcPriceForBranch(s).toLocaleString()}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="radio" checked={redeemKind === "package"} onChange={() => setRedeemKind("package")} data-testid="gift-purchase-redeem-package" />
            <span className="text-sm w-16 pt-1">Package</span>
            <div className="flex-1 relative">
              <Input
                value={selectedPackage ? selectedPackage.name : packageQ}
                onChange={(e) => { setRedeemKind("package"); setSelectedPackage(null); setPackageQ(e.target.value); }}
                placeholder="Type a package name…"
                data-testid="gift-purchase-package-input"
              />
              {redeemKind === "package" && !selectedPackage && pkgMatches.length > 0 && (
                <div className="absolute z-20 left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-md max-h-52 overflow-auto">
                  {pkgMatches.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => { setSelectedPackage(p); setPackageQ(""); }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted"
                      data-testid={`gift-purchase-package-pick-${p.id}`}
                    >
                      {p.name}
                      <span className="ml-2 text-xs text-muted-foreground">Rp {pkgPriceForBranch(p).toLocaleString()}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </label>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Service or Package name is stamped onto the card in place of the IDR value.</p>
      </div>

      <div className="flex items-end justify-between gap-3 pt-2 border-t border-border">
        <div>
          <Label className="text-xs">Quantity</Label>
          <Input
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value || "1", 10)))}
            className="w-24"
            data-testid="gift-purchase-quantity"
          />
        </div>
        <Button onClick={submit} data-testid="gift-purchase-add">Add to Order</Button>
      </div>
    </div>
  );
}
